import { useMedia } from 'react-use';
import { useKeenSlider } from 'keen-slider/react';
import React, { useState } from 'react';

import { Box, Show } from '@chakra-ui/react';

import StyledButton from '../../components/Button/Button';
import {
    StyledSection,
    StyledHeading,
    StyledHighlight,
    TextContainer,
    StyledContainer,
    CaseStudiesWrapper,
    CaseStudiesSlide,
    SliderControlDiv,
} from './styles';
import CaseStudyItem from '../../components/CaseStudyItem/CaseStudyItem';
import { BaseSection } from '../../components/BaseSection';
import { BoxProps } from '@chakra-ui/react';

export interface ButtonProps {
    type: string;
    href: string;
    target: string;
    label: string;
}
export interface CaseStudySectionProps extends BoxProps {
    title: string;
    text: string;
    casestudies: CaseStudyItem[];
    bgColor?: string;
    padding?: string;
    type?: string;
    buttons?: ButtonProps[];
}

function Arrow(props: { disabled: boolean; left?: boolean; onClick: (e: any) => void }) {
    const disabled = props.disabled ? ' arrow--disabled' : '';
    return (
        <svg
            onClick={props.onClick}
            className={`arrow ${props.left ? 'arrow--left' : 'arrow--right'} ${disabled}`}
            xmlns="http://www.w3.org/2000/svg"
            width="8"
            height="13"
            viewBox="0 0 8 13"
        >
            {props.left && (
                <path d="M8.00001 0.730469L5.54012 0.730469L0.363648 6.74354L5.54012 12.7305L8.00001 12.7305L2.82354 6.74354L8.00001 0.730469Z" />
            )}
            {!props.left && (
                <path d="M1.52588e-05 12.7305H2.45991L7.63638 6.7174L2.45991 0.730469H1.52588e-05L5.17649 6.7174L1.52588e-05 12.7305Z" />
            )}
        </svg>
    );
}

const CaseStudySection = ({
    title,
    text,
    casestudies,
    bgColor,
    padding,
    type,
    buttons,
    ...props
}: CaseStudySectionProps) => {
    const isMobile = useMedia('(max-width: 780px)');

    const [currentSlide, setCurrentSlide] = React.useState(0);
    const [loaded, setLoaded] = useState(false);
    const [keenSliderRef, instanceRef] = useKeenSlider<HTMLDivElement>({
        initial: 0,
        slideChanged(slider) {
            setCurrentSlide(slider.track.details.rel);
        },
        created() {
            setLoaded(true);
        },
    });

    return (
        <Box
            _before={{
                content: '""',
                position: 'absolute',
                height: '100%',
                top: 0,
                width: '100vw',
                marginLeft: '50%',
                left: '-50vw',
                zIndex: -2,
                backgroundColor:
                    bgColor || bgColor === 'white' || type === 'slider'
                        ? 'white'
                        : 'rgba(238, 238, 238, 1)',
            }}
            position={'relative'}
            {...props}
        >
            <TextContainer>
                <StyledHeading>{title}</StyledHeading>
                <StyledHighlight>{text}</StyledHighlight>
            </TextContainer>
            {type !== 'slider' ? (
                <StyledContainer $flex="row">
                    <StyledButton
                        href="https://www.idosell.com/pl/historie-klientow-idosell/"
                        style="default"
                        target="_blank"
                    >
                        Poznaj historie klientów
                    </StyledButton>
                    <StyledButton
                        href="https://www.idosell.com/pl/galeria-designow/"
                        style="outline"
                        target="_blank"
                    >
                        Zobacz nasze realizacje
                    </StyledButton>
                </StyledContainer>
            ) : null}
            {isMobile ? (
                <>
                    <CaseStudiesWrapper ref={keenSliderRef} className="keen-slider">
                        {casestudies.map((casestudy, index) => (
                            <div key={index} className={`keen-slider__slide number-slide${index}`}>
                                <CaseStudyItem
                                    title={casestudy.title}
                                    image={casestudy.image}
                                    hastags={casestudy.hastags}
                                    link={casestudy.link}
                                    key={index}
                                    type={type === 'slider' ? 'slider' : 'default'}
                                />
                            </div>
                        ))}
                    </CaseStudiesWrapper>
                    <SliderControlDiv>
                        {loaded && instanceRef.current && (
                            <>
                                <div
                                    style={{
                                        cursor: 'pointer',
                                        width: '20px',
                                    }}
                                >
                                    <Arrow
                                        left
                                        onClick={(e: any) =>
                                            e.stopPropagation() || instanceRef.current?.prev()
                                        }
                                        disabled={currentSlide === 0}
                                    />
                                </div>
                                <span
                                    style={{
                                        margin: '0px 30px',
                                    }}
                                >
                                    {currentSlide + 1} / {casestudies.length}{' '}
                                </span>
                                <div
                                    style={{
                                        cursor: 'pointer',
                                        width: '20px',
                                    }}
                                >
                                    <Arrow
                                        onClick={(e: any) =>
                                            e.stopPropagation() || instanceRef.current?.next()
                                        }
                                        disabled={
                                            currentSlide ===
                                            instanceRef.current.track.details.slides.length - 1
                                        }
                                    />
                                </div>
                            </>
                        )}
                    </SliderControlDiv>
                </>
            ) : (
                <>
                    <Box mb={'50px'}>
                        <CaseStudiesWrapper ref={keenSliderRef} className="keen-slider">
                            {casestudies.map((casestudy, index) => {
                                if (index % 3 === 0) {
                                    const set = casestudies.slice(index, index + 3);

                                    return (
                                        <CaseStudiesSlide
                                            key={index}
                                            className={`keen-slider__slide number-slide${index}`}
                                        >
                                            {set.map((study, setIndex) => (
                                                <CaseStudyItem
                                                    key={index + setIndex}
                                                    title={study.title}
                                                    image={study.image}
                                                    hastags={study.hastags}
                                                    link={study.link}
                                                />
                                            ))}
                                        </CaseStudiesSlide>
                                    );
                                }

                                return null;
                            })}
                        </CaseStudiesWrapper>
                    </Box>
                    {casestudies.length > 3 && (
                        <SliderControlDiv>
                            {loaded && instanceRef.current && (
                                <>
                                    <div style={{ cursor: 'pointer', width: '20px' }}>
                                        <Arrow
                                            left
                                            onClick={(e: any) =>
                                                e.stopPropagation() || instanceRef.current?.prev()
                                            }
                                            disabled={currentSlide === 0}
                                        />
                                    </div>
                                    <span style={{ margin: '0px 30px' }}>
                                        {currentSlide + 1} / {Math.ceil(casestudies.length / 3)}
                                    </span>
                                    <div style={{ cursor: 'pointer', width: '20px' }}>
                                        <Arrow
                                            onClick={(e: any) =>
                                                e.stopPropagation() || instanceRef.current?.next()
                                            }
                                            disabled={
                                                currentSlide === Math.floor(casestudies.length / 3)
                                            }
                                        />
                                    </div>
                                </>
                            )}
                        </SliderControlDiv>
                    )}
                </>
            )}
            {type === 'slider' ? (
                <Box mt="30px">
                    <StyledContainer $flex="row">
                        <StyledButton
                            href={
                                buttons
                                    ? buttons[0].href
                                    : 'https://www.idosell.com/pl/historie-klientow-idosell/'
                            }
                            style="default"
                            target="_blank"
                        >
                            {buttons ? buttons[0].label : 'Poznaj historie klientów'}
                        </StyledButton>
                        <StyledButton
                            href={
                                buttons
                                    ? buttons[1].href
                                    : 'https://www.idosell.com/pl/galeria-designow/'
                            }
                            style="outline"
                            target="_blank"
                        >
                            {buttons ? buttons[1].label : 'Zobacz nasze realizacje'}
                        </StyledButton>
                    </StyledContainer>
                </Box>
            ) : null}
        </Box>
    );
};

export default CaseStudySection;
